import { Minus, Plus, Trash2, X, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { eur } from "../lib/api";

const FREE_SHIP = 100;
const SHIP_COST = 10.95;

export const CartDrawer = () => {
  const { items, open, setOpen, setQty, remove, subtotal } = useCart();
  const nav = useNavigate();
  const pct = Math.min(100, (subtotal / FREE_SHIP) * 100);
  const shipping = subtotal >= FREE_SHIP ? 0 : SHIP_COST;

  const checkout = () => { setOpen(false); nav("/finalizar-compra"); };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer" data-lenis-prevent side="right" className="w-full sm:max-w-md bg-[#0E2A1C] border-l border-[#8CD05A]/20 p-0 flex flex-col [&>button]:hidden">
        <div className="flex items-center justify-between px-6 h-[72px] border-b border-[#8CD05A]/15">
          <SheetTitle className="font-label text-3xl font-normal text-label-gold">Tu carrito</SheetTitle>
          <button data-testid="cart-drawer-close" onClick={() => setOpen(false)} className="w-10 h-10 rounded-full border border-[#8CD05A]/30 grid place-items-center hover:border-[#F2B441]"><X className="w-4 h-4" /></button>
        </div>
        <SheetDescription className="sr-only">Productos en tu carrito</SheetDescription>
        <div className="px-6 py-4 border-b border-[#8CD05A]/15">
          <p className="text-xs text-[#C9D6C2] mb-2" data-testid="cart-shipping-progress">
            {subtotal >= FREE_SHIP ? "Envío gratuito desbloqueado" : `Te faltan ${eur(FREE_SHIP - subtotal)} para el envío gratis`}
          </p>
          <div className="h-1 bg-[#1A4430]"><div className="h-full bg-[#8CD05A] transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {items.length === 0 && <p data-testid="cart-empty" className="text-[#C9D6C2] py-16 text-center font-serif-d italic text-2xl">Tu carrito está vacío.</p>}
          {items.map((i) => (
            <div key={i.id} data-testid={`cart-item-row-${i.id}`} className="flex gap-4 border-b border-[#8CD05A]/10 pb-4">
              <img src={i.image} alt={i.name} className="w-20 h-24 object-cover border border-[#F2B441]/40" />
              <div className="flex-1 flex flex-col">
                <div className="flex justify-between gap-2">
                  <div><p className="font-label text-xl leading-none">{i.name}</p><p className="text-xs text-[#C9D6C2] mt-1">Formato {i.size}</p></div>
                  <button data-testid={`cart-item-remove-${i.id}`} onClick={() => remove(i.id)} className="text-[#C9D6C2] hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="flex items-center justify-between mt-auto">
                  <div className="flex items-center border border-[#8CD05A]/25 rounded-full">
                    <button data-testid={`cart-item-decrement-${i.id}`} onClick={() => setQty(i.id, i.qty - 1)} className="w-8 h-8 grid place-items-center"><Minus className="w-3 h-3" /></button>
                    <span data-testid={`cart-item-qty-${i.id}`} className="w-6 text-center text-sm font-mono-d">{i.qty}</span>
                    <button data-testid={`cart-item-increment-${i.id}`} onClick={() => setQty(i.id, i.qty + 1)} className="w-8 h-8 grid place-items-center"><Plus className="w-3 h-3" /></button>
                  </div>
                  <span className="font-mono-d text-sm">{eur(i.price * i.qty)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="px-6 py-6 border-t border-[#8CD05A]/15 space-y-4">
          <div className="flex justify-between text-sm text-[#C9D6C2]">
            <span>Subtotal</span><span className="font-mono-d">{eur(subtotal)}</span>
          </div>
          <div className="flex justify-between text-sm text-[#C9D6C2]">
            <span>Envío</span>
            <span data-testid="cart-shipping" className="font-mono-d">{!items.length ? "—" : shipping === 0 ? "Gratis" : eur(shipping)}</span>
          </div>
          <div className="flex justify-between items-baseline border-t border-[#F6EEDC]/10 pt-3">
            <span className="text-sm">Total</span>
            <span data-testid="cart-subtotal" className="font-serif-d text-4xl">{eur(subtotal + (items.length ? shipping : 0))}</span>
          </div>
          <p className="text-xs text-[#C9D6C2]">Envío 10,95 € · gratis en compras desde 100 €. Paga con tarjeta, Bizum, PayPal o transferencia.</p>
          <button data-testid="cart-checkout-button" disabled={!items.length} onClick={checkout}
            className="w-full h-14 rounded-full bg-[#8CD05A] text-[#0E2A1C] font-semibold hover:bg-[#F2B441] transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
            Finalizar compra <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
